import { Link, useParams } from "react-router-dom";
import NewsLetter from "../../components/newsletter";
import useSWR from "swr";
import { URLS } from "../../services/urls";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import ApplyButton from "../../components/actions/ApplyButton";
import { API } from "../../services/axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faBriefcase, faMapMarkerAlt, faClock, faLayerGroup, 
  faUserGraduate, faMoneyBillWave, faCalendarAlt, faShareAlt 
} from "@fortawesome/free-solid-svg-icons";
import { 
  faFacebookF, faTwitter, faWhatsapp, faLinkedinIn 
} from "@fortawesome/free-brands-svg-icons";
import JobCard from "../../components/jobs/JobCard"; // Réutilisation de votre carte moderne
import "./JobDetStyle.css";

const JobDetails = () => {
  const { jobId } = useParams();
  const [similar, setSimilar] = useState([]);
  const { t } = useTranslation();

  const { data, isLoading } = useSWR(URLS.JOBS.details(jobId));

  // Chargement des jobs similaires
  useEffect(() => {
    if (data?.industry?.name) {
      API.get(URLS.JOBS.byIndustry(data.industry.name)).then((resp) => {
        // On exclut le job actuel de la liste
        const filtered = resp?.data?.results?.filter(j => j.slug !== jobId) || [];
        setSimilar(filtered);
      });
    }
  }, [data, jobId]);

  if (isLoading) {
    return (
        <div className="d-flex justify-content-center align-items-center vh-100">
            <div className="spinner-border text-primary" role="status"></div>
        </div>
    );
  }

  return (
    <>
      <section className="section-box-2 pt-50 pb-50 job-header-section">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-8">
              <div className="d-flex align-items-center mb-3">
                 <span className="badge bg-soft-primary text-primary me-2 px-3 py-2 rounded-pill">
                    {data?.type || "Temps Plein"}
                 </span>
                 <span className="text-muted small">
                    <FontAwesomeIcon icon={faClock} className="me-1" />
                    Publié {moment(data?.created_at).fromNow()}
                 </span>
              </div>
              <h2 className="mb-10 text-dark fw-bold">{data?.title}</h2>
              <div className="font-md text-muted">
                <span className="me-4">
                    <FontAwesomeIcon icon={faBriefcase} className="me-2" />
                    {data?.work_place || "Sur site"}
                </span>
                <span>
                    <FontAwesomeIcon icon={faMapMarkerAlt} className="me-2" />
                    {data?.city?.name}, {data?.country?.name}
                </span>
              </div>
            </div>
            
            <div className="col-lg-4 text-lg-end mt-4 mt-lg-0">
              {data?.closed ? (
                <button className="btn btn-secondary btn-lg disabled w-100 w-lg-auto">
                    {t("jobExpired")}
                </button>
              ) : (
                <div className="btn-apply-wrapper">
                    <ApplyButton jobId={jobId} job={data} />
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="section-box mt-50 mb-50">
        <div className="container">
          <div className="row">
            
            {/* COLONNE GAUCHE : Description */}
            <div className="col-lg-8 col-md-12 col-12 mb-5">
              <div className="job-content-card bg-white p-4 p-md-5 rounded-4 shadow-sm border">
                
                <h4 className="mb-4 fw-bold text-dark">{t("textDescription")}</h4>
                <div 
                    className="content-text text-muted mb-5"
                    dangerouslySetInnerHTML={{ __html: data?.description }}
                ></div>

                {data?.responsabilities && (
                    <>
                        <h4 className="mb-4 fw-bold text-dark">{t("textResponsabilities")}</h4>
                        <div 
                            className="content-text text-muted mb-5"
                            dangerouslySetInnerHTML={{ __html: data?.responsabilities }}
                        ></div>
                    </>
                )}

                <h4 className="mb-4 fw-bold text-dark">{t("tetSkills")}</h4>
                <div className="d-flex flex-wrap gap-2 mb-5">
                  {data?.skills?.map((skill) => (
                    <span key={skill?.id} className="badge bg-light text-dark border px-3 py-2">
                      {skill?.name}
                    </span>
                  ))}
                </div>

                {/* Partage */}
                <div className="border-top pt-4 mt-4 d-flex align-items-center flex-wrap gap-3">
                    <span className="fw-bold text-dark">
                        <FontAwesomeIcon icon={faShareAlt} className="me-2" />
                        {t("textShareJob")}:
                    </span>
                    <a href={`https://www.facebook.com/sharer.php?u=${window.location.href}`} target="_blank" className="btn-social-share fb">
                        <FontAwesomeIcon icon={faFacebookF} />
                    </a>
                    <a href={`https://twitter.com/intent/tweet?url=${window.location.href}`} target="_blank" className="btn-social-share tw">
                        <FontAwesomeIcon icon={faTwitter} />
                    </a>
                    <a href={`https://www.linkedin.com/shareArticle?mini=true&url=${window.location.href}`} target="_blank" className="btn-social-share ln">
                        <FontAwesomeIcon icon={faLinkedinIn} />
                    </a>
                    <a href={`whatsapp://send?text=${window.location.href}`} target="_blank" className="btn-social-share wa">
                        <FontAwesomeIcon icon={faWhatsapp} />
                    </a>
                </div>

              </div>
            </div>

            {/* COLONNE DROITE : Sidebar Sticky */}
            <div className="col-lg-4 col-md-12 col-12">
              <div className="sidebar-sticky-wrapper">
                  
                  {/* Carte Infos Clés */}
                  <div className="sidebar-card bg-white p-4 rounded-4 shadow-sm border mb-4">
                    <h5 className="mb-4 fw-bold text-dark border-bottom pb-3">
                        {t("textEmploymentInfo")}
                    </h5>
                    
                    <ul className="list-unstyled sidebar-info-list">
                        <li>
                            <div className="icon-wrapper"><FontAwesomeIcon icon={faLayerGroup} /></div>
                            <div>
                                <span className="d-block text-muted small">{t("textIndustry")}</span>
                                <span className="fw-bold text-dark">{data?.industry?.name}</span>
                            </div>
                        </li>
                        <li>
                            <div className="icon-wrapper"><FontAwesomeIcon icon={faBriefcase} /></div>
                            <div>
                                <span className="d-block text-muted small">{t("textJobLevel")}</span>
                                <span className="fw-bold text-dark">{data?.experience_level}</span>
                            </div>
                        </li>
                        <li>
                            <div className="icon-wrapper"><FontAwesomeIcon icon={faUserGraduate} /></div>
                            <div>
                                <span className="d-block text-muted small">{t("textExperience")}</span>
                                <span className="fw-bold text-dark">{data?.experience} {t("textYears")}</span>
                            </div>
                        </li>
                        {data?.salary_min && (
                            <li>
                                <div className="icon-wrapper"><FontAwesomeIcon icon={faMoneyBillWave} /></div>
                                <div>
                                    <span className="d-block text-muted small">{t("textSalary")}</span>
                                    <span className="fw-bold text-dark">{data.salary_min} XAF</span>
                                </div>
                            </li>
                        )}
                        <li>
                            <div className="icon-wrapper"><FontAwesomeIcon icon={faCalendarAlt} /></div>
                            <div>
                                <span className="d-block text-muted small">{t("textDeadline")}</span>
                                <span className="fw-bold text-danger">
                                    {moment(data?.application_end).format('DD MMMM YYYY')}
                                </span>
                            </div>
                        </li>
                    </ul>
                  </div>

                  {/* Carte Localisation (Simple) */}
                  <div className="sidebar-card bg-white p-4 rounded-4 shadow-sm border">
                     <h5 className="mb-3 fw-bold text-dark">Localisation</h5>
                     <div className="d-flex align-items-start">
                        <FontAwesomeIcon icon={faMapMarkerAlt} className="text-primary mt-1 me-3" />
                        <p className="mb-0 text-muted">
                            {data?.city?.name}, {data?.country?.name}
                        </p>
                     </div>
                  </div>

              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION JOBS SIMILAIRES */}
      {similar && similar.length > 0 && (
        <section className="section-box mt-50 mb-50 bg-light py-5">
            <div className="container">
                <h3 className="mb-40 fw-bold">{t("textSimilarJobs")}</h3>
                <div className="row">
                    {similar.slice(0, 4).map((sim) => (
                        <div key={"sim" + sim.slug} className="col-xl-3 col-lg-4 col-md-6 mb-4">
                            <JobCard job={sim} />
                        </div>
                    ))}
                </div>
            </div>
        </section>
      )}

      <NewsLetter />
    </>
  );
};

export default JobDetails;